import { useState, useEffect } from "react"
import InfoBox from "../../ui/InfoBox"
import ImportClaimForm from "./ImportClaimForm"
import NewPriorityForm from "./NewPriorityForm"
import SavedClaimBox from "./SavedClaimBox"

interface PriorityClaimBoxProps {
  claims?: any[]
  initialData?: any[]
  onChange?: (claims: any[]) => void
}

export default function PriorityClaimBox({
  claims,
  onChange,
  initialData
}: PriorityClaimBoxProps) {

  const [isOpen, setIsOpen] = useState(false)
  const [claimType, setClaimType] = useState<"import" | "new" | null>(null)
  const [savedClaims, setSavedClaims] = useState<any[]>([])
  const [editingClaim, setEditingClaim] = useState<any | null>(null)

  useEffect(() => {

    const mapClaim = (c: any) => ({

      ...c,

      type: "priority",

      isImported: c.filingDate === null && c.attachmentFileName == null,

      country: c.office || "",

      markName:
        c.markName ||
        c.filingNumber ||
        c.applicationNumber ||
        c.registrationNumber ||
        ""

    })

    if (initialData && initialData.length > 0) {

      setSavedClaims(initialData.map(mapClaim))
      return

    }

    if (claims) {

      setSavedClaims(claims.map(mapClaim))

    }

  }, [claims, initialData])

  const closeForm = () => {
    setIsOpen(false)
    setClaimType(null)
    setEditingClaim(null)
  }

  const pushUp = (updated: any[]) => {
    setSavedClaims(updated)
    if (onChange) onChange(updated)
  }

  const handleImportSubmit = ({
    office,
    filingNumber,
    filingDate
  }: {
    office: string
    filingNumber: string
    filingDate?: string
  }) => {

    const newClaim = {
      type: "priority",
      country: office,
      markName: filingNumber,
      office,
      filingNumber,
      filingDate: filingDate ?? null,
      attachmentFile: null,
      isImported: true,
      isDeleted: false
    }

    let updated

    if (editingClaim) {

      updated = savedClaims.map((c) =>
        c === editingClaim ? { ...newClaim } : c
      )

    } else {

      updated = [...savedClaims, newClaim]

    }

    pushUp(updated)
    closeForm()

  }

  const handleNewSubmit = (data: {
    office: string
    filingDate: string
    filingNumber: string
    attachmentFile?: File | null
  }) => {

    const newClaim = {
      ...data,
      country: data.office,
      markName: data.filingNumber,
      type: "priority",
      isImported: false,
      isDeleted: false
    }

    let updated

    if (editingClaim) {

      updated = savedClaims.map((c) =>
        c === editingClaim ? { ...newClaim } : c
      )

    } else {

      updated = [...savedClaims, newClaim]

    }

    pushUp(updated)
    closeForm()

  }

  return (

    <div className="flex flex-col gap-4">

      <div className="flex items-center gap-2">

        <h3 className="text-base font-semibold text-gray-800">
          Priority claim
        </h3>

        <InfoBox title="Priority claim">
          You can claim priority from an earlier national or international filing
        </InfoBox>

      </div>

      <div
        onClick={() => setIsOpen(true)}
        className="border rounded p-8 flex flex-col items-center cursor-pointer bg-gray-50 hover:border-orange-500 transition w-60"
      >

        <div className="text-4xl text-gray-700 mb-2">
          +
        </div>

        <span className="text-sm font-medium text-gray-700">
          Add priority claim
        </span>

      </div>

      {savedClaims.length > 0 && (

        <div className="flex flex-wrap gap-3">

          {savedClaims.map((claim, index) =>
            !claim.isDeleted && (

              <SavedClaimBox
                key={index}
                claim={claim}

                onEdit={() => {
                  setEditingClaim(claim)
                  setIsOpen(true)
                  setClaimType(claim.isImported ? "import" : "new")
                }}

                onDelete={() => {

                  const updated = savedClaims.map((c, i) =>
                    i === index ? { ...c, isDeleted: true } : c
                  )

                  pushUp(updated)

                }}

              />

            )
          )}

        </div>

      )}

      {isOpen && (

        <div className="relative border rounded p-6 bg-white text-sm text-gray-700 space-y-4">

          <button
            onClick={closeForm}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-lg"
          >
            ×
          </button>

          <p className="font-semibold">
            {editingClaim
              ? "Edit your priority claim"
              : "How would you like to add your priority claim?"}
          </p>

          {!editingClaim && (

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">

              <button
                onClick={() => setClaimType("import")}
                className={`px-5 py-2 rounded border text-sm font-medium ${
                  claimType === "import"
                    ? "bg-blue-700 text-white border-blue-700"
                    : "text-blue-700 border-blue-700 hover:bg-blue-50"
                }`}
              >
                Import from existing application
              </button>

              <button
                onClick={() => setClaimType("new")}
                className={`px-5 py-2 rounded border text-sm font-medium ${
                  claimType === "new"
                    ? "bg-blue-700 text-white border-blue-700"
                    : "text-blue-700 border-blue-700 hover:bg-blue-50"
                }`}
              >
                Create new priority
              </button>

            </div>

          )}

          {claimType === "import" && (

            <ImportClaimForm
              type="priority"
              onCancel={closeForm}
              onSubmit={handleImportSubmit}
              labelCountry="Country of earlier filing"
              labelTrademark="Filing number or mark name"
              submitText={editingClaim ? "Save changes" : "Add priority"}
            />

          )}

          {claimType === "new" && (

            <NewPriorityForm
              onCancel={closeForm}
              onSubmit={handleNewSubmit}
            />

          )}

        </div>

      )}

    </div>

  )

}